import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';
import {
    McpRealtimeStatus,
    McpTranscriptEntry,
} from 'app/core/services/mcp-realtime.service';
import { McpRealtimeRouterService } from 'app/core/services/realtime-segments/mcp-realtime-router.service';
import { McpTraceService, McpTraceEvent } from 'app/core/services/mcp-trace.service';

@Component({
    selector: 'mcp-drawer',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './mcp-drawer.component.html',
    styleUrls: ['./mcp-drawer.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class McpDrawerComponent implements OnInit, OnDestroy {
    @ViewChild('transcriptContainer') transcriptContainer: ElementRef;
    @ViewChild('textInput') textInput: ElementRef;
    @ViewChild('debugContainer') debugContainer: ElementRef;

    isOpen = false;
    showDebug = true;
    status: McpRealtimeStatus = 'idle';
    transcript: McpTranscriptEntry[] = [];
    traceEvents: McpTraceEvent[] = [];
    aiSpeaking = false;
    micMuted = true;
    textMessage = '';

    private destroy$ = new Subject<void>();

    constructor(
        readonly realtimeService: McpRealtimeRouterService,
        private readonly traceService: McpTraceService,
    ) {}

    ngOnInit(): void {
        this.traceService.log('debug_panel', {}, { detail: 'Debug panel enabled' });

        this.realtimeService.status$
            .pipe(takeUntil(this.destroy$))
            .subscribe((s) => (this.status = s));

        this.realtimeService.transcript$
            .pipe(takeUntil(this.destroy$))
            .subscribe((t) => {
                this.transcript = t;
                setTimeout(() => this.scrollToBottom(), 50);
            });

        this.realtimeService.aiSpeaking$
            .pipe(takeUntil(this.destroy$))
            .subscribe((s) => (this.aiSpeaking = s));

        this.realtimeService.micMuted$
            .pipe(takeUntil(this.destroy$))
            .subscribe((m) => (this.micMuted = m));

        this.traceService.events$
            .pipe(takeUntil(this.destroy$))
            .subscribe((events) => {
                this.traceEvents = events;
                setTimeout(() => this.scrollDebugToBottom(), 50);
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggle(): void {
        this.isOpen = !this.isOpen;
    }

    close(): void {
        this.isOpen = false;
    }

    async onSessionToggle(): Promise<void> {
        if (this.status === 'active') {
            this.realtimeService.stop();
        } else if (this.status === 'idle' || this.status === 'error') {
            await this.realtimeService.start();
        }
    }

    onPushToTalkDown(): void {
        if (this.status === 'active') {
            this.realtimeService.setMicMuted(false);
        }
    }

    onPushToTalkUp(): void {
        if (this.status === 'active') {
            this.realtimeService.setMicMuted(true);
        }
    }

    onSendText(): void {
        const msg = this.textMessage.trim();
        if (!msg) return;
        this.textMessage = '';
        this.realtimeService.sendText(msg);
    }

    onTextKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.onSendText();
        }
    }

    clearTrace(): void {
        this.traceService.clear();
    }

    trackTrace(index: number, ev: McpTraceEvent): string {
        return ev.at + ev.type;
    }

    formatTime(iso: string): string {
        try {
            const d = new Date(iso);
            return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
        } catch {
            return '';
        }
    }

    formatData(data: Record<string, any>): string {
        try {
            const str = JSON.stringify(data, null, 0);
            return str.length > 300 ? str.substring(0, 300) + '…' : str;
        } catch {
            return '';
        }
    }

    get sessionIcon(): string {
        switch (this.status) {
            case 'active': return 'power_settings_new';
            case 'connecting': return 'hourglass_empty';
            case 'error': return 'error_outline';
            default: return 'power_settings_new';
        }
    }

    get sessionTooltip(): string {
        switch (this.status) {
            case 'active': return 'Disconnect session';
            case 'connecting': return 'Connecting...';
            case 'error': return 'Connection failed — click to retry';
            default: return 'Connect session';
        }
    }

    get statusLabel(): string {
        switch (this.status) {
            case 'active':
                if (this.aiSpeaking) return 'Speaking...';
                return this.micMuted ? 'Hold mic to talk' : 'Listening...';
            case 'connecting': return 'Connecting';
            case 'error': return 'Error';
            default: return 'Ready';
        }
    }

    private scrollToBottom(): void {
        if (this.transcriptContainer?.nativeElement) {
            const el = this.transcriptContainer.nativeElement;
            el.scrollTop = el.scrollHeight;
        }
    }

    private scrollDebugToBottom(): void {
        if (this.debugContainer?.nativeElement) {
            const el = this.debugContainer.nativeElement;
            el.scrollTop = el.scrollHeight;
        }
    }
}
